// =============================================================================
// PEDRA — src/components/SettingsModal.tsx
// Modal "Configuracoes": tudo o que nao cabe no painel rapido da barra lateral.
// Prioridade da otimizacao, orientacao do corte, unidade, ordem das dimensoes,
// casas decimais, sobra minima aproveitavel, agrupamento de chapas iguais,
// tempo maximo de calculo, semente e tema.
// =============================================================================

import { useCallback, useEffect, useId, useState } from 'react';
import type { CSSProperties, ChangeEvent, ReactElement } from 'react';

import type {
  CutOrientation,
  DimensionOrder,
  OptimizationPriority,
  Settings,
  StackMode,
  Unit,
} from '../core/types';
import {
  DEFAULT_DECIMALS,
  fromMicron,
  fromMicronTrim,
  isImperial,
  kerfToText,
  MICRON_PER_MM,
  UNIT_LABELS,
  UNIT_SUFFIX,
} from '../core/units';
import { LIMITS } from '../core/validation';
import {
  cutOrientationLabel,
  dimensionOrderLabel,
  priorityLabel,
  stackModeLabel,
} from '../lib/format';
import { Modal } from './Modal';
import { DimensionField, HelpHint, SelectField, ToggleField } from './OptionsPanel';

export interface SettingsModalProps {
  open: boolean;
  settings: Settings;
  onChange(patch: Partial<Settings>): void;
  onReset(): void;
  onClose(): void;
  /**
   * Tema atual. Opcional: quando nao vier, a secao "Aparencia" nao aparece.
   * Quem controla o tema e o `App`, nao as `Settings` do projeto.
   */
  theme?: 'light' | 'dark';
  onToggleTheme?(): void;
}

// -----------------------------------------------------------------------------
// LISTAS DE OPCOES
// -----------------------------------------------------------------------------

const PRIORITIES: readonly OptimizationPriority[] = ['leastWaste', 'leastCuts', 'smallerSheets'];
const CUT_ORIENTATIONS: readonly CutOrientation[] = ['optimal', 'preferWidth', 'preferLength'];
const UNITS: readonly Unit[] = ['mm', 'cm', 'm', 'in', 'ft'];
const DIMENSION_ORDERS: readonly DimensionOrder[] = ['lengthFirst', 'widthFirst'];
const STACK_MODES: readonly StackMode[] = ['auto', 'never', 'always'];

/** Medida de referencia dos exemplos: a chapa de pedra de 3070 × 1900 mm. */
const SAMPLE_LENGTH = 3070 * MICRON_PER_MM;

/**
 * Exemplos de casas decimais no sistema metrico, escritos NA UNIDADE ATUAL.
 * Um rotulo fixo em milimetro ("2 — 1.200,50") nao ajuda quem trabalha em
 * metro: aqui ele le "2 — 3,07" e ve na hora o que vai ganhar ou perder.
 */
function metricDecimalOptions(unit: Unit): ReadonlyArray<{ value: string; label: string }> {
  return [0, 1, 2, 3].map((d) => ({
    value: String(d),
    label: `${d} — ${fromMicron(SAMPLE_LENGTH, unit, d)}`,
  }));
}

/** No sistema imperial as "casas" viram o denominador da fracao. */
const IMPERIAL_DECIMALS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '0', label: 'Polegada inteira — 16"' },
  { value: '1', label: 'Até 1/8 — 16 1/8"' },
  { value: '2', label: 'Até 1/16 — 16 1/16"' },
  { value: '3', label: 'Até 1/32 — 16 1/32"' },
];

const HELP = {
  priority:
    'O que o otimizador tenta minimizar primeiro. "Menor área desperdiçada" ' +
    'aproveita melhor a chapa; "Menor número de cortes" agiliza a serra; ' +
    '"Usar as chapas menores" preserva as chapas grandes para outro serviço.',
  cutOrientation:
    'Direção preferida do primeiro corte de cada chapa. "Ideal" testa as duas ' +
    'e fica com a melhor. As outras duas ajudam quando a seccionadora exige ' +
    'tiras em um sentido só.',
  unit:
    'Unidade usada para digitar e exibir as medidas. Trocar aqui CONVERTE o ' +
    'que já foi digitado (3,07 m vira 3070 mm e volta exato), nunca reinterpreta ' +
    'o número. As casas decimais são ajustadas junto. A espessura de corte ' +
    'continua sendo informada em milímetros.',
  dimensionOrder:
    'Ordem em que as duas medidas aparecem na tabela, no desenho e nos ' +
    'relatórios.',
  decimals:
    'Quantos dígitos aparecem depois da vírgula. Em polegadas e pés define o ' +
    'denominador da fração exibida (1/8, 1/16, 1/32).',
  minTrim:
    'Menor lado que uma sobra precisa ter para ser considerada aproveitável. ' +
    'Sobras com qualquer lado menor que isso contam como desperdício.',
  stack:
    '"Automático" agrupa quando três ou mais chapas têm exatamente o mesmo ' +
    'plano de corte; "Sempre" agrupa a partir de duas; "Nunca" desenha uma ' +
    'chapa por vez.',
  timeBudget:
    'Teto de tempo do cálculo. Ao estourar, o resultado devolvido é o melhor ' +
    'encontrado até ali. Projetos com muitas peças pedem mais tempo.',
  seed:
    'Semente do sorteio interno. A mesma semente com as mesmas peças produz ' +
    'exatamente o mesmo plano de corte. Troque para explorar outras soluções.',
  theme: 'Alterna entre o tema claro e o escuro da interface.',
} as const;

/**
 * O rodape do modal e um flex com quebra de linha. `order: 1` joga o aviso da
 * confirmacao para depois do "Concluido" e a base de 100% o poe numa linha so,
 * em vez de espremer os botoes e empurra-los para baixo.
 */
const CONFIRM_NOTE_STYLE: CSSProperties = { flex: '1 1 100%', order: 1, margin: 0 };

/**
 * Proxima semente sem `Math.random()`: um LCG classico (Numerical Recipes),
 * previsivel e suficiente para "sortear outro plano".
 */
function nextSeed(seed: number): number {
  const s = Number.isFinite(seed) ? Math.abs(Math.trunc(seed)) : 1;
  return (Math.imul(s, 1664525) + 1013904223) >>> 8;
}

// -----------------------------------------------------------------------------
// COMPONENTE
// -----------------------------------------------------------------------------

export function SettingsModal(props: SettingsModalProps): ReactElement {
  const { open, settings, onChange, onReset, onClose, theme, onToggleTheme } = props;
  const uid = useId();

  // Restaurar padroes e destrutivo para as CONFIGURACOES, entao segue o mesmo
  // padrao do "Limpar" das tabelas: o primeiro clique arma, o segundo executa,
  // e sair do botao desarma. Sem `window.confirm`, que o app nao usa.
  const [confirmReset, setConfirmReset] = useState(false);

  // Reabrir o modal sempre comeca com o botao desarmado.
  useEffect(() => {
    if (!open) setConfirmReset(false);
  }, [open]);

  const handleReset = useCallback((): void => {
    if (!confirmReset) {
      setConfirmReset(true);
      return;
    }
    setConfirmReset(false);
    onReset();
  }, [confirmReset, onReset]);

  const seconds = Math.max(1, Math.min(60, Math.round(settings.timeBudgetMs / 1000)));
  const decimalOptions = isImperial(settings.unit)
    ? IMPERIAL_DECIMALS
    : metricDecimalOptions(settings.unit);

  // Exemplo ao vivo: uma chapa de pedra de 3070 × 1900 mm. As medidas vivem em
  // Micron, entao o exemplo e formatado do mesmo jeito que a tela — inclusive
  // aparando zeros a direita, para o usuario ver "3,07 m" e nao "3,070 m".
  const previewLength = 3070 * MICRON_PER_MM;
  const previewWidth = 1900 * MICRON_PER_MM;
  const first = settings.dimensionOrder === 'widthFirst' ? previewWidth : previewLength;
  const second = settings.dimensionOrder === 'widthFirst' ? previewLength : previewWidth;
  const a = fromMicronTrim(first, settings.unit, settings.decimalPlaces);
  const b = fromMicronTrim(second, settings.unit, settings.decimalPlaces);
  const preview = isImperial(settings.unit)
    ? `${a} × ${b}`
    : `${a} × ${b} ${UNIT_SUFFIX[settings.unit]}`;

  const handleUnitChange = (raw: string): void => {
    const unit = raw as Unit;
    if (unit === settings.unit) return;
    // As medidas sao guardadas em Micron (1/1000 mm), independentes da unidade:
    // trocar a unidade so muda como o mesmo valor e escrito na tela. Por isso
    // 3,07 m -> 3070 mm -> 3,07 m volta exato, sem deriva de arredondamento.
    // O que precisa acompanhar sao as casas decimais: metro com 1 casa (ou
    // milimetro com 3) deixa a leitura ruim, entao aplicamos o padrao da
    // unidade nova. O kerf nao entra nessa conversao — e sempre em mm.
    onChange({ unit, decimalPlaces: DEFAULT_DECIMALS[unit] });
  };

  return (
    <Modal
      open={open}
      title="Configurações"
      onClose={onClose}
      size="lg"
      footer={
        <>
          <button
            type="button"
            className={`btn${confirmReset ? ' btn--danger' : ' btn--ghost'}`}
            onClick={handleReset}
            onBlur={() => setConfirmReset(false)}
            title="Volta todas as configurações aos valores de fábrica. Suas peças e chapas não são apagadas."
          >
            {confirmReset ? 'Confirmar restauração?' : 'Restaurar padrões'}
          </button>
          {confirmReset ? (
            <span className="field__help" role="status" style={CONFIRM_NOTE_STYLE}>
              Todas as configurações voltam aos valores de fábrica. Suas peças, chapas e fitas
              continuam como estão.
            </span>
          ) : null}
          <span className="modal-footer__spacer" />
          <button type="button" className="btn btn--primary" onClick={onClose}>
            Concluído
          </button>
        </>
      }
    >
      {/* ------------------------------------------------------------------ */}
      <section className="modal-section">
        <h3 className="modal-section__title">Otimização</h3>

        <SelectField
          id={`${uid}-priority`}
          label="Prioridade de otimização"
          help={HELP.priority}
          value={settings.priority}
          options={PRIORITIES.map((p) => ({ value: p, label: priorityLabel(p) }))}
          onChange={(v) => onChange({ priority: v as OptimizationPriority })}
        />

        <SelectField
          id={`${uid}-cutorientation`}
          label="Orientação do corte"
          help={HELP.cutOrientation}
          value={settings.cutOrientation}
          options={CUT_ORIENTATIONS.map((c) => ({ value: c, label: cutOrientationLabel(c) }))}
          onChange={(v) => onChange({ cutOrientation: v as CutOrientation })}
        />

        <div className="opt-field">
          <div className="opt-field__head">
            <label className="opt-field__label" htmlFor={`${uid}-time`}>
              Tempo máximo de cálculo
            </label>
            <HelpHint text={HELP.timeBudget} />
          </div>
          <div className="modal-slider">
            <input
              id={`${uid}-time`}
              className="modal-slider__input"
              type="range"
              min={1}
              max={60}
              step={1}
              value={seconds}
              title={HELP.timeBudget}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onChange({ timeBudgetMs: Number(e.target.value) * 1000 })
              }
            />
            <output className="modal-slider__value tabular" htmlFor={`${uid}-time`}>
              {seconds} s
            </output>
          </div>
        </div>

        <div className="opt-field">
          <div className="opt-field__head">
            <label className="opt-field__label" htmlFor={`${uid}-seed`}>
              Semente do cálculo
            </label>
            <HelpHint text={HELP.seed} />
          </div>
          <div className="modal-seed">
            <input
              id={`${uid}-seed`}
              className="opt-input tabular"
              type="number"
              min={0}
              step={1}
              value={settings.seed}
              title={HELP.seed}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const n = Number(e.target.value);
                if (Number.isFinite(n)) onChange({ seed: Math.max(0, Math.trunc(n)) });
              }}
            />
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => onChange({ seed: nextSeed(settings.seed) })}
              title="Gera outra semente e, com ela, outro arranjo possível"
            >
              Sortear outra
            </button>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      <section className="modal-section">
        <h3 className="modal-section__title">Medidas e exibição</h3>

        <SelectField
          id={`${uid}-unit`}
          label="Unidade"
          help={HELP.unit}
          value={settings.unit}
          options={UNITS.map((u) => ({ value: u, label: UNIT_LABELS[u] }))}
          onChange={handleUnitChange}
        />

        <SelectField
          id={`${uid}-order`}
          label="Ordem das dimensões"
          help={HELP.dimensionOrder}
          value={settings.dimensionOrder}
          options={DIMENSION_ORDERS.map((d) => ({ value: d, label: dimensionOrderLabel(d) }))}
          onChange={(v) => onChange({ dimensionOrder: v as DimensionOrder })}
        />

        <SelectField
          id={`${uid}-decimals`}
          label="Casas decimais"
          help={HELP.decimals}
          value={String(settings.decimalPlaces)}
          options={decimalOptions}
          onChange={(v) => onChange({ decimalPlaces: Number(v) })}
        />

        <p className="modal-preview">
          Uma chapa de 3070 × 1900 mm aparece assim:{' '}
          <strong className="tabular">{preview}</strong>
          <br />
          Espessura de corte: sempre em milímetros (atual:{' '}
          <strong className="tabular">{kerfToText(settings.kerf)} mm</strong>).
        </p>
      </section>

      {/* ------------------------------------------------------------------ */}
      <section className="modal-section">
        <h3 className="modal-section__title">Sobras e agrupamento</h3>

        <DimensionField
          id={`${uid}-mintrim`}
          label="Dimensão mínima de sobra aproveitável"
          help={HELP.minTrim}
          value={settings.minTrimDimension}
          unit={settings.unit}
          decimals={settings.decimalPlaces}
          min={0}
          max={LIMITS.MAX_DIMENSION}
          onCommit={(minTrimDimension) => onChange({ minTrimDimension })}
        />

        <SelectField
          id={`${uid}-stack`}
          label="Empilhar chapas com o mesmo plano de corte"
          help={HELP.stack}
          value={settings.stackSamePanels}
          options={STACK_MODES.map((m) => ({ value: m, label: stackModeLabel(m) }))}
          onChange={(v) => onChange({ stackSamePanels: v as StackMode })}
        />
      </section>

      {/* ------------------------------------------------------------------ */}
      {theme && onToggleTheme ? (
        <section className="modal-section">
          <h3 className="modal-section__title">Aparência</h3>
          <div className="opt-toggles">
            <ToggleField
              id={`${uid}-theme`}
              label="Tema escuro"
              help={HELP.theme}
              checked={theme === 'dark'}
              onChange={onToggleTheme}
            />
          </div>
        </section>
      ) : null}
    </Modal>
  );
}

export default SettingsModal;
